import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { widgetApi } from '@/api/widgetApi';
import type { WidgetTheme, Widget } from '@/api/services/widgetService';

// Widget state type definition
interface WidgetState {
  widget: Widget | null;
  isLoading: boolean;
  updating: boolean;
  error: string | null;
  unsavedChanges: boolean;
}

// Initial state
const initialState: WidgetState = {
  widget: null,
  isLoading: false,
  updating: false,
  error: null,
  unsavedChanges: false,
};

// Async thunks
export const fetchWidgetConfiguration = createAsyncThunk(
  'widget/fetchConfiguration',
  async (_, { rejectWithValue }) => {
    try {
      const response = await widgetApi.getWidgetTheme();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch widget configuration');
    }
  }
);

export const updateWidgetTheme = createAsyncThunk(
  'widget/updateTheme',
  async ({ widgetId, themeId, updates }: { widgetId: string, themeId: string, updates: any }, { rejectWithValue }) => {
    try {
      // Set widgetId in the updates
      const payload = {
        ...updates,
        widgetId
      };
      const response = await widgetApi.updateWidgetTheme(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update widget theme');
    }
  }
);

export const updateWidgetSettings = createAsyncThunk(
  'widget/updateSettings',
  async ({ widgetId, updates }: { widgetId: string, updates: any }, { rejectWithValue }) => {
    try {
      // Here we would make an API call to update widget settings
      // For now we'll reuse the update theme endpoint
      const response = await widgetApi.updateWidgetTheme({
        ...updates,
        widgetId
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update widget settings');
    }
  }
);

// Widget slice
const widgetSlice = createSlice({
  name: 'widget',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<WidgetTheme>) {
      if (state.widget && state.widget.widgettheme && state.widget.widgettheme.length > 0) {
        state.widget.widgettheme[0] = action.payload;
      } else if (state.widget) {
        state.widget.widgettheme = [action.payload];
      }
      state.unsavedChanges = true;
    },
    updateThemeProperty(state, action: PayloadAction<{ path: string[]; value: any }>) {
      const { path, value } = action.payload;
      if (!state.widget || !state.widget.widgettheme || !state.widget.widgettheme[0]) return;
      
      let current: any = state.widget.widgettheme[0];
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
      state.unsavedChanges = true;
    },
    resetUnsavedChanges(state) {
      state.unsavedChanges = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWidgetConfiguration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWidgetConfiguration.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload && action.payload.data) {
          state.widget = action.payload.data;
          console.log('Widget configuration fetched successfully:', state.widget);
        }
      })
      .addCase(fetchWidgetConfiguration.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Unknown error occurred';
      })
      .addCase(updateWidgetTheme.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateWidgetTheme.fulfilled, (state, action) => {
        state.updating = false;
        state.unsavedChanges = false;

        // Ensure proper handling of the widget response data structure
        if (action.payload && action.payload.data) {
          // The response might be the theme directly or contain a full widget
          if (action.payload.data.widgettheme) {
            // It's a full widget response
            state.widget = action.payload.data;
          } else if (state.widget) {
            // It's a theme-only response, update the theme in the existing widget
            if (!state.widget.widgettheme) {
              state.widget.widgettheme = [action.payload.data as any]; // Use type assertion to avoid type error
            } else {
              // Make a proper deep copy to ensure state update is tracked
              state.widget.widgettheme[0] = JSON.parse(JSON.stringify(action.payload.data));
            }
          }
          console.log('Widget theme updated successfully:', state.widget);
        }
      })
      .addCase(updateWidgetTheme.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string || 'Unknown error occurred';
      })
      .addCase(updateWidgetSettings.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateWidgetSettings.fulfilled, (state, action) => {
        state.updating = false;
        state.unsavedChanges = false;
        
        // Similar handling for widget settings updates
        if (action.payload && action.payload.data) {
          if (action.payload.data.widgettheme) {
            state.widget = action.payload.data;
          } else if (state.widget) {
            // Update just the non-theme properties with proper copy
            state.widget = {
              ...state.widget,
              ...JSON.parse(JSON.stringify(action.payload.data))
            };
          }
          console.log('Widget settings updated successfully:', state.widget);
        }
      })
      .addCase(updateWidgetSettings.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string || 'Unknown error occurred';
      });
  },
});

export const { setTheme, updateThemeProperty, resetUnsavedChanges } = widgetSlice.actions;
export default widgetSlice.reducer;
